import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { Form, message, Modal } from 'antd';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
    getProjectSprints,
    getProjectSprintsForPhase,
    getProjectSprintsForMilestone,
    getProjectSprintsStandalone,
    getProjectSprint,
    createProjectSprint,
    updateProjectSprint,
    createProjectSprintTask,
    updateProjectSprintTask,
    completeProjectSprintTask,
    revertProjectSprintTaskToTodo,
    sortProjectSprintTask,
    getProjectSprintTasks,
    getProjectSprintTaskDetail,
    deleteProjectSprintTask,
    deleteProjectSprint,
    assignProjectSprintTask,
    getProjectTasks,
    getProjectPhases,
    getProjectMilestones,
} from '@services/projects.service';
import { useProjectTask } from '@hooks/projects/task/useProjectTask';
import {
    invalidateProjectQueries,
    invalidateProjectTaskQueries,
    invalidateProjectQueriesExceptIssueUpgradeList,
} from '@hooks/projects/projectQueryInvalidation';
import { useProjectDetail } from '@hooks/projects/detail/useProjectDetail';
import { useProjectSprintSubmission } from '@hooks/projects/useProjectSprintSubmission';
import { useProjectTaskConsultation } from '@hooks/projects/useProjectTaskConsultation';
import { useCanCheck } from '@/ability/helpers';
import {
    PROJECT_SPRINTS_VIEW_ABILITY,
    PROJECT_SPRINTS_CREATE_ABILITY,
    PROJECT_SPRINTS_UPDATE_ABILITY,
    PROJECT_SPRINTS_DELETE_ABILITY,
    PROJECT_TASKS_CREATE_ABILITY,
    PROJECT_TASKS_UPDATE_ABILITY,
    PROJECT_TASKS_DELETE_ABILITY,
    PROJECT_TASKS_MARK_TODO_ABILITY,
    PROJECT_TASKS_ASSIGN_ABILITY,
} from '@/ability';
import { useAuthStore } from '@stores/auth.store';
import { contentToApiMentions } from '@utils/richTextMentions';
import { resolveDescriptionMentionsForDisplay } from '@utils/richTextMentions';
import { validateLinkUrlInput } from '@utils/linkUrlValidation.jsx';
import {
    getSprintTaskApiId,
    buildSprintDescription,
    buildTaskDescription,
    truncateFileName,
    getTaskDescriptionMarkdown,
    getSprintDescriptionText,
    getSprintAttachmentList,
    isLikelyImageAttachmentUrl,
    resolveAttachmentUrl,
    buildSprintSubmittedColumnSubmissionLayout,
    mergeSprintDetailIntoSprints,
    normalizeProjectSprintDetailPayload,
} from '@hooks/projects/projectHelpers';
const SPRINT_CREATE_DRAFT_STORAGE_KEY = 'project-sprints:create-sprint-draft:v1';
const TASK_CREATE_DRAFT_STORAGE_KEY = 'project-sprints:create-task-draft:v1';

function getStorageValue(key) {
    if (typeof window === 'undefined') return null;
    try {
        return window.localStorage.getItem(key);
    } catch (_err) {
        return null;
    }
}

function setStorageValue(key, value) {
    if (typeof window === 'undefined') return;
    try {
        window.localStorage.setItem(key, value);
    } catch (_err) {
        // ignore localStorage failures
    }
}

function removeStorageValue(key) {
    if (typeof window === 'undefined') return;
    try {
        window.localStorage.removeItem(key);
    } catch (_err) {
        // ignore localStorage failures
    }
}

function normalizeBinaryFiles(files) {
    if (!Array.isArray(files)) return [];
    return files
        .map((f) => f?.originFileObj ?? f?.file ?? f)
        .filter((f) => f instanceof File || f instanceof Blob);
}

function normalizeLinkAttachments(attachments) {
    if (!Array.isArray(attachments)) return [];
    return attachments
        .filter((att) => att && att.url)
        .filter((att) => {
            const type = String(att.type || 'link').toLowerCase();
            return type === 'link';
        })
        .map((att) => ({
            type: 'link',
            url: att.url,
            ...(att.title ? { title: att.title } : {}),
        }));
}

function hasAnyFileTypeAttachment(attachments) {
    if (!Array.isArray(attachments)) return false;
    return attachments.some((att) => {
        if (!att || !att.url) return false;
        const type = String(att.type || 'link').toLowerCase();
        return type !== 'link';
    });
}

/** Syncs horizontal scroll between sprint Kanban header row and body row (required for sticky headers + wide boards). */
export function useSprintKanbanSyncHorizontalScroll() {
    const headerScrollRef = useRef(null);
    const bodyScrollRef = useRef(null);
    const syncing = useRef(false);

    const onHeaderScroll = useCallback((e) => {
        if (syncing.current) return;
        const body = bodyScrollRef.current;
        if (!body) return;
        syncing.current = true;
        body.scrollLeft = e.currentTarget.scrollLeft;
        queueMicrotask(() => {
            syncing.current = false;
        });
    }, []);

    const onBodyScroll = useCallback((e) => {
        if (syncing.current) return;
        const head = headerScrollRef.current;
        if (!head) return;
        syncing.current = true;
        head.scrollLeft = e.currentTarget.scrollLeft;
        queueMicrotask(() => {
            syncing.current = false;
        });
    }, []);

    return { headerScrollRef, bodyScrollRef, onHeaderScroll, onBodyScroll };
}

// --- Sprint task mutations (project feature; lives with project hooks) ---

export function useUpdateSprintTask(projectId) {
    const queryClient = useQueryClient();

    const invalidate = () => {
        invalidateProjectTaskQueries(queryClient, projectId);
    };

    const updateMutation = useMutation({
        mutationFn: ({ sprintId, taskId, ...body }) => {
            const files = normalizeBinaryFiles(body.files);
            const forceMultipart = hasAnyFileTypeAttachment(body.attachments);
            const linkAttachments = normalizeLinkAttachments(body.attachments);
            if (files?.length || forceMultipart) {
                const formData = new FormData();
                if (body.title != null) formData.append('title', typeof body.title === 'string' ? body.title : '');
                if (body.description) formData.append('description', JSON.stringify(body.description));
                if (body.type) formData.append('type', body.type);
                if (body.priority) formData.append('priority', body.priority);
                if (linkAttachments.length > 0) formData.append('attachmentUrls', JSON.stringify(linkAttachments));
                files.forEach((file) => formData.append('files', file));
                return updateProjectSprintTask(projectId, sprintId, taskId, formData);
            }
            const { files: _f, attachments: _a, ...rest } = body;
            return updateProjectSprintTask(projectId, sprintId, taskId, {
                ...rest,
                ...(linkAttachments.length > 0 && { attachments: linkAttachments }),
            });
        },
        onSuccess: invalidate,
    });

    const completeMutation = useMutation({
        mutationFn: ({ sprintId, taskId }) => completeProjectSprintTask(projectId, sprintId, taskId),
        onSuccess: invalidate,
    });

    const revertMutation = useMutation({
        mutationFn: ({ sprintId, taskId }) => revertProjectSprintTaskToTodo(projectId, sprintId, taskId),
        onSuccess: invalidate,
    });

    const deleteMutation = useMutation({
        mutationFn: ({ sprintId, taskId }) => deleteProjectSprintTask(projectId, sprintId, taskId),
        onSuccess: invalidate,
    });

    const sortMutation = useMutation({
        mutationFn: ({ sprintId, taskId, sortOrder }) =>
            sortProjectSprintTask(projectId, sprintId, taskId, { sortOrder }),
        onSuccess: invalidate,
    });

    const assignMutation = useMutation({
        mutationFn: ({ sprintId, taskId, assigneeId }) =>
            assignProjectSprintTask(projectId, sprintId, taskId, { assigneeId: String(assigneeId) }),
        onSuccess: invalidate,
    });

    const updateTask = (sprintId, taskId, body, callbacks = {}) => {
        updateMutation.mutate(
            { sprintId, taskId, ...body },
            {
                onSuccess: callbacks.onSuccess,
                onError: callbacks.onError,
            },
        );
    };

    const completeTask = (sprintId, taskId, callbacks = {}) => {
        completeMutation.mutate(
            { sprintId, taskId },
            {
                onSuccess: callbacks.onSuccess,
                onError: callbacks.onError,
            },
        );
    };

    const revertTaskToTodo = (sprintId, taskId, callbacks = {}) => {
        revertMutation.mutate(
            { sprintId, taskId },
            {
                onSuccess: callbacks.onSuccess,
                onError: callbacks.onError,
            },
        );
    };

    const deleteTask = (sprintId, taskId, callbacks = {}) => {
        deleteMutation.mutate(
            { sprintId, taskId },
            {
                onSuccess: callbacks.onSuccess,
                onError: callbacks.onError,
            },
        );
    };

    const sortTask = (sprintId, taskId, sortOrder, callbacks = {}) => {
        sortMutation.mutate(
            { sprintId, taskId, sortOrder },
            {
                onSuccess: callbacks.onSuccess,
                onError: callbacks.onError,
            },
        );
    };

    const assignTask = (sprintId, taskId, assigneeId, callbacks = {}) => {
        assignMutation.mutate(
            { sprintId, taskId, assigneeId },
            {
                onSuccess: callbacks.onSuccess,
                onError: callbacks.onError,
            },
        );
    };

    return {
        updateTask,
        updateTaskPending: updateMutation.isPending,
        completeTask,
        completeTaskPending: completeMutation.isPending,
        revertTaskToTodo,
        revertTaskToTodoPending: revertMutation.isPending,
        deleteTask,
        deleteTaskPending: deleteMutation.isPending,
        sortTask,
        sortTaskPending: sortMutation.isPending,
        assignTask,
        assignTaskPending: assignMutation.isPending,
    };
}

// useCreateSprintTask was moved to separate hook useProjectTask

/** @param {string} status */
function normalizeSprintTaskStatus(status) {
    const s = String(status ?? '').toLowerCase();
    if (s === 'done') return 'completed';
    if (s === 'task_consultation') return 'consultation';
    return s;
}

/**
 * Partition sprint tasks into Kanban columns (todo / completed / submitted to QA).
 * @param {Array} tasks
 */
export function partitionSprintTasks(tasks) {
    const list = Array.isArray(tasks) ? tasks : [];
    const todo = [];
    const completed = [];
    const consultation = [];
    const submitted = [];
    for (const t of list) {
        const s = normalizeSprintTaskStatus(t?.status);
        if (s === 'submitted_to_qa' || s === 'submitted_to_pm') submitted.push(t);
        else if (s === 'completed') completed.push(t);
        else if (s === 'consultation') consultation.push(t);
        else todo.push(t);
    }
    return { todo, completed, consultation, submitted };
}

/**
 * Milestone + sprint underline tabs (selection only). Pair with GET `/projects/:id/sprints/:sprintId` for board data.
 */
export function useProjectSprintTabSelection(milestones, baseSprints, options = {}) {
    const { useMilestonesEnabled = true, projectMilestones = [], selectedMilestoneId: controlledMilestoneId, setSelectedMilestoneId: controlledSetMilestoneId } = options;
    const [selectedMilestoneIdLocal, setSelectedMilestoneIdLocal] = useState(null);
    const [selectedSprintId, setSelectedSprintId] = useState(null);

    const selectedMilestoneId = controlledMilestoneId !== undefined ? controlledMilestoneId : selectedMilestoneIdLocal;
    const setSelectedMilestoneId = controlledSetMilestoneId !== undefined ? controlledSetMilestoneId : setSelectedMilestoneIdLocal;

    const milestoneList = useMemo(() => (Array.isArray(milestones) ? milestones : []), [milestones]);
    const sprintList = useMemo(() => (Array.isArray(baseSprints) ? baseSprints : []), [baseSprints]);

    const sprintsForMilestone = useMemo(() => {
        return sprintList;
    }, [sprintList]);

    /** Same frame as milestone/sprint list changes — avoids null selected sprint before effects run. */
    const selectedSprintIdResolved = useMemo(() => {
        if (!sprintsForMilestone.length) return null;
        if (
            selectedSprintId != null &&
            sprintsForMilestone.some((s) => String(s.id) === String(selectedSprintId))
        ) {
            return selectedSprintId;
        }
        return sprintsForMilestone[0]?.id ?? null;
    }, [sprintsForMilestone, selectedSprintId]);

    useEffect(() => {
        if (!milestoneList.length) {
            setSelectedMilestoneId(null);
            return;
        }
        setSelectedMilestoneId((prev) => {
            if (prev != null && milestoneList.some((m) => String(m.id) === String(prev))) return prev;
            return milestoneList[0]?.id ?? null;
        });
    }, [milestoneList]);

    useEffect(() => {
        if (!sprintsForMilestone.length) {
            setSelectedSprintId(null);
            return;
        }
        setSelectedSprintId((prev) => {
            if (prev != null && sprintsForMilestone.some((s) => String(s.id) === String(prev))) return prev;
            return sprintsForMilestone[0]?.id ?? null;
        });
    }, [sprintsForMilestone]);

    return {
        selectedMilestoneId,
        setSelectedMilestoneId,
        selectedSprintId,
        setSelectedSprintId,
        selectedSprintIdResolved,
        sprintsForMilestone,
    };
}

/**
 * Milestone + sprint tab selection and Kanban drag-drop transitions for Project Sprints tab.
 */
export function useProjectSprintsKanbanBoard({
    milestones = [],
    sprints = [],
    selectedMilestoneId,
    setSelectedMilestoneId,
    selectedSprintId,
    setSelectedSprintId,
    completeTask,
    revertTaskToTodo,
    updateTask,
    sortTask,
    onRequestSubmitToQA,
    onRequestConsultation,
    canMarkTodo = true,
    canReorderTodo = true,
    useMilestonesEnabled = true,
    projectMilestones = [],
}) {
    const [dropTarget, setDropTarget] = useState(null);
    const [draggingColumn, setDraggingColumn] = useState(null);
    const [optimisticSprints, setOptimisticSprints] = useState([]);
    const optimisticPendingRef = useRef(0);

    const milestoneList = useMemo(() => (Array.isArray(milestones) ? milestones : []), [milestones]);
    const sprintList = useMemo(() => (Array.isArray(sprints) ? sprints : []), [sprints]);

    // Keep an optimistic working copy so drops update UI immediately.
    // When API calls settle, server refetch will reconcile, but we also rollback on failure.
    useEffect(() => {
        // If we have pending optimistic ops, avoid clobbering local state mid-flight.
        if (optimisticPendingRef.current > 0) return;
        setOptimisticSprints(sprintList);
    }, [sprintList]);

    const findSprintIndexById = useCallback((list, sprintId) => {
        const id = String(sprintId ?? '');
        return list.findIndex((s) => String(s?.id ?? '') === id);
    }, []);

    const updateTaskStatusOptimistic = useCallback(
        (list, { sprintId, taskId, nextStatus }) => {
            const next = Array.isArray(list) ? list.slice() : [];
            const sprintIdx = findSprintIndexById(next, sprintId);
            if (sprintIdx < 0) return next;
            const sprint = next[sprintIdx];
            const tasks = Array.isArray(sprint?.tasks) ? sprint.tasks.slice() : [];
            const tid = String(taskId ?? '');
            const taskIdx = tasks.findIndex(
                (t) =>
                    String(getSprintTaskApiId(t) ?? '') === tid ||
                    String(t?.id ?? '') === tid ||
                    String(t?.taskId ?? '') === tid,
            );
            if (taskIdx < 0) return next;
            const task = tasks[taskIdx];
            tasks[taskIdx] = { ...task, status: nextStatus };
            next[sprintIdx] = { ...sprint, tasks };
            return next;
        },
        [findSprintIndexById],
    );

    const reorderTodoTasksOptimistic = useCallback(
        (list, { sprintId, taskId, targetTaskId }) => {
            const next = Array.isArray(list) ? list.slice() : [];
            const sprintIdx = findSprintIndexById(next, sprintId);
            if (sprintIdx < 0) return next;
            const sprint = next[sprintIdx];
            const tasks = Array.isArray(sprint?.tasks) ? sprint.tasks.slice() : [];
            if (tasks.length < 2) return next;

            const taskIdStr = String(taskId ?? '');
            const targetTaskIdStr = String(targetTaskId ?? '');
            if (!taskIdStr || !targetTaskIdStr || taskIdStr === targetTaskIdStr) return next;

            const withMeta = tasks.map((task, index) => ({
                task,
                index,
                id: String(getSprintTaskApiId(task) ?? task?.id ?? task?.taskId ?? ''),
                status: normalizeSprintTaskStatus(task?.status),
            }));
            const todoItems = withMeta.filter((entry) => entry.status === 'todo');
            const fromIdx = todoItems.findIndex((entry) => entry.id === taskIdStr);
            const toIdx = todoItems.findIndex((entry) => entry.id === targetTaskIdStr);
            if (fromIdx < 0 || toIdx < 0 || fromIdx === toIdx) return next;

            const reorderedTodo = todoItems.slice();
            const [moved] = reorderedTodo.splice(fromIdx, 1);
            reorderedTodo.splice(toIdx, 0, moved);
            const rankById = new Map(reorderedTodo.map((entry, idx) => [entry.id, idx]));

            const sorted = withMeta
                .slice()
                .sort((a, b) => {
                    const aTodo = a.status === 'todo';
                    const bTodo = b.status === 'todo';
                    if (aTodo && bTodo) {
                        return (rankById.get(a.id) ?? 0) - (rankById.get(b.id) ?? 0);
                    }
                    return a.index - b.index;
                })
                .map((entry) => entry.task);

            next[sprintIdx] = { ...sprint, tasks: sorted };
            return next;
        },
        [findSprintIndexById],
    );

    const toPromise = useCallback((fn) => {
        return new Promise((resolve, reject) => {
            try {
                fn({
                    onSuccess: resolve,
                    onError: reject,
                });
            } catch (err) {
                reject(err);
            }
        });
    }, []);

    const sprintsForMilestone = useMemo(() => {
        return optimisticSprints;
    }, [optimisticSprints]);

    const selectedSprint = useMemo(
        () => sprintsForMilestone.find((s) => String(s.id) === String(selectedSprintId)) ?? null,
        [sprintsForMilestone, selectedSprintId],
    );

    const {
        todo: todoTasks,
        completed: completedTasks,
        consultation: consultationTasks,
        submitted: submittedTasks,
    } = useMemo(() => partitionSprintTasks(selectedSprint?.tasks), [selectedSprint?.tasks]);

    const clearDropTarget = useCallback(() => {
        setDropTarget(null);
        setDraggingColumn(null);
    }, []);

    const handleTaskMove = useCallback(
        ({ sprintId, taskId, sourceColumn, targetColumn, targetTaskId, selectedTaskIds }) => {
            clearDropTarget();
            if (!sprintId || !taskId || !sourceColumn || !targetColumn) return;

            const sprint = optimisticSprints.find((s) => String(s.id) === String(sprintId));
            const task = Array.isArray(sprint?.tasks)
                ? sprint.tasks.find(
                    (t) =>
                        String(getSprintTaskApiId(t) ?? '') === String(taskId) ||
                        String(t?.id ?? '') === String(taskId) ||
                        String(t?.taskId ?? '') === String(taskId),
                )
                : null;
            if (!task) {
                message.error('Task not found');
                return;
            }

            const apiTaskId = getSprintTaskApiId(task) ?? String(taskId);
            const onErr = (err) =>
                message.error(err?.response?.data?.message || err?.message || 'Failed to update task');

            if (sourceColumn === 'todo' && targetColumn === 'todo') {
                if (!canReorderTodo) {
                    message.warning("You don't have permission to reorder Todo tasks.");
                    return;
                }
                const targetId = String(targetTaskId ?? '');
                if (!targetId || targetId === String(apiTaskId)) return;
                const todoItems = partitionSprintTasks(sprint?.tasks).todo;
                const todoIds = todoItems.map((t) => String(getSprintTaskApiId(t) ?? t?.id ?? t?.taskId ?? ''));
                const nextIndex = todoIds.findIndex((id) => id === targetId);
                if (nextIndex < 0) return;
                const sortOrder = nextIndex + 1;
                const prev = optimisticSprints;
                optimisticPendingRef.current += 1;
                setOptimisticSprints((cur) =>
                    reorderTodoTasksOptimistic(cur, {
                        sprintId,
                        taskId: apiTaskId,
                        targetTaskId: targetId,
                    }),
                );
                void toPromise((cb) => sortTask(sprintId, apiTaskId, sortOrder, cb)).catch((err) => {
                    setOptimisticSprints(prev);
                    onErr(err);
                }).finally(() => {
                    optimisticPendingRef.current = Math.max(0, optimisticPendingRef.current - 1);
                });
                return;
            }

            if (sourceColumn === targetColumn) return;

            const runOptimistic = async ({ nextStatus, apiCall, successMessage }) => {
                const prev = optimisticSprints;
                optimisticPendingRef.current += 1;
                setOptimisticSprints((cur) =>
                    updateTaskStatusOptimistic(cur, { sprintId, taskId: apiTaskId, nextStatus }),
                );
                try {
                    await apiCall();
                    if (successMessage) message.success(successMessage);
                } catch (err) {
                    setOptimisticSprints(prev);
                    onErr(err);
                } finally {
                    optimisticPendingRef.current = Math.max(0, optimisticPendingRef.current - 1);
                }
            };

            if (sourceColumn === 'todo' && targetColumn === 'completed') {
                if (!canMarkTodo) {
                    message.warning("You don't have permission to mark task todo/completed.");
                    return;
                }
                void runOptimistic({
                    nextStatus: 'completed',
                    apiCall: () => toPromise((cb) => completeTask(sprintId, apiTaskId, cb)),
                    successMessage: 'Moved to completed',
                });
                return;
            }
            if (sourceColumn === 'completed' && targetColumn === 'todo') {
                if (!canMarkTodo) {
                    message.warning("You don't have permission to mark task todo/completed.");
                    return;
                }
                void runOptimistic({
                    nextStatus: 'todo',
                    apiCall: () => toPromise((cb) => revertTaskToTodo(sprintId, apiTaskId, cb)),
                    successMessage: 'Moved to todo',
                });
                return;
            }
            if (sourceColumn === 'completed' && targetColumn === 'submitted') {
                onRequestSubmitToQA?.({ sprintId, taskId: apiTaskId, task, sourceColumn, selectedTaskIds });
                return;
            }
            if (sourceColumn === 'todo' && targetColumn === 'submitted') {
                onRequestSubmitToQA?.({ sprintId, taskId: apiTaskId, task, sourceColumn });
                return;
            }
            if (targetColumn === 'consultation') {
                onRequestConsultation?.({ sprintId, taskId: apiTaskId, task, sourceColumn });
                return;
            }
            if (sourceColumn === 'consultation') {
                if (targetColumn === 'todo') {
                    void runOptimistic({
                        nextStatus: 'todo',
                        apiCall: () => toPromise((cb) => updateTask(sprintId, apiTaskId, { status: 'todo' }, cb)),
                        successMessage: 'Moved to todo',
                    });
                    return;
                }
                if (targetColumn === 'completed') {
                    void runOptimistic({
                        nextStatus: 'completed',
                        apiCall: () => toPromise((cb) => updateTask(sprintId, apiTaskId, { status: 'completed' }, cb)),
                        successMessage: 'Moved to completed',
                    });
                    return;
                }
                if (targetColumn === 'submitted') {
                    onRequestSubmitToQA?.({ sprintId, taskId: apiTaskId, task, sourceColumn });
                    return;
                }
            }
            if (sourceColumn === 'submitted' && targetColumn === 'completed') {
                if (!canMarkTodo) {
                    message.warning("You don't have permission to mark task todo/completed.");
                    return;
                }
                void runOptimistic({
                    nextStatus: 'completed',
                    apiCall: () => toPromise((cb) => updateTask(sprintId, apiTaskId, { status: 'completed' }, cb)),
                    successMessage: 'Moved to completed',
                });
                return;
            }
            if (sourceColumn === 'submitted' && targetColumn === 'todo') {
                if (!canMarkTodo) {
                    message.warning("You don't have permission to mark task todo/completed.");
                    return;
                }
                void runOptimistic({
                    nextStatus: 'todo',
                    apiCall: () => toPromise((cb) => updateTask(sprintId, apiTaskId, { status: 'todo' }, cb)),
                    successMessage: 'Moved to todo',
                });
                return;
            }
            message.warning('This move is not supported');
        },
        [
            clearDropTarget,
            optimisticSprints,
            updateTaskStatusOptimistic,
            toPromise,
            completeTask,
            revertTaskToTodo,
            updateTask,
            onRequestSubmitToQA,
            canMarkTodo,
            canReorderTodo,
            sortTask,
            reorderTodoTasksOptimistic,
        ],
    );

    const handleDndDragStart = useCallback(({ sourceColumn }) => {
        setDraggingColumn(sourceColumn);
        setDropTarget(null);
    }, []);

    const handleDndDragOverColumn = useCallback((columnId) => {
        if (!columnId) {
            setDropTarget(null);
            return;
        }
        setDropTarget(columnId);
    }, []);

    const handleDndDragEnd = useCallback(
        ({ sprintId, taskId, sourceColumn, targetColumn, targetTaskId, selectedTaskIds }) => {
            setDraggingColumn(null);
            setDropTarget(null);
            handleTaskMove({ sprintId, taskId, sourceColumn, targetColumn, targetTaskId, selectedTaskIds });
        },
        [handleTaskMove],
    );

    return {
        selectedMilestoneId,
        setSelectedMilestoneId,
        selectedSprintId,
        setSelectedSprintId,
        sprintsForMilestone,
        selectedSprint,
        todoTasks,
        completedTasks,
        consultationTasks,
        submittedTasks,
        dropTarget,
        draggingColumn,
        clearDropTarget,
        handleTaskMove,
        handleDndDragStart,
        handleDndDragOverColumn,
        handleDndDragEnd,
        onRequestSubmitToQA,
    };
}

const MENTION_USERS_LIMIT = 100;

/**
 * Sprint / task / link modal state and handlers for Project Sprints tab.
 */
export function useProjectSprintsTabModals(
    projectId,
    {
        project,
        milestones,
        sprints,
        createSprint,
        createSprintPending,
        updateSprint,
        updateSprintPending,
        createSprintModalOpen,
        setCreateSprintModalOpen,
        /** Milestone chosen via the sprint tab pill row (create sprint uses this; no modal dropdown). */
        tabSelectedMilestoneId,
        updateTask,
        updateTaskPending,
        useMilestonesEnabled = true,
        usePhasesEnabled = false,
        phases = [],
    },
) {
    const enrichDescriptionAnchorsForEditor = useCallback((html, sprint) => {
        if (!html || typeof html !== 'string' || typeof document === 'undefined') return html || '';
        const attachmentList = getSprintAttachmentList(sprint);
        const attachmentByUrl = new Map();
        attachmentList.forEach((a) => {
            const url = typeof a?.url === 'string' ? a.url.trim() : '';
            if (!url) return;
            attachmentByUrl.set(url, String(a?.type || '').toLowerCase());
        });
        const root = document.createElement('div');
        root.innerHTML = html;
        root.querySelectorAll('a').forEach((a) => {
            const href = (a.getAttribute('href') || '').trim();
            if (!href) return;
            if (href.startsWith('#mention-user-') || href.startsWith('#mention-milestone-') || href.startsWith('#mention-sprint-')) {
                return;
            }
            if (href.startsWith('#attachment-')) {
                a.setAttribute('data-attachment', 'true');
                if (!a.getAttribute('data-attachment-kind')) a.setAttribute('data-attachment-kind', 'file');
                return;
            }
            if (attachmentByUrl.has(href)) {
                const attachmentType = attachmentByUrl.get(href);
                if (attachmentType === 'link') {
                    if (isLikelyImageAttachmentUrl(href)) {
                        a.setAttribute('data-attachment', 'true');
                        a.setAttribute('data-attachment-kind', 'image');
                    } else {
                        a.setAttribute('data-link', 'true');
                    }
                    return;
                }
                a.setAttribute('data-attachment', 'true');
                const kind = attachmentType === 'image' || isLikelyImageAttachmentUrl(href) ? 'image' : 'file';
                a.setAttribute('data-attachment-kind', kind);
                return;
            }
            if (isLikelyImageAttachmentUrl(href)) {
                a.setAttribute('data-attachment', 'true');
                a.setAttribute('data-attachment-kind', 'image');
            } else {
                a.setAttribute('data-link', 'true');
            }
        });
        return root.innerHTML;
    }, []);

    const { createTask, createTaskPending } = useProjectTask(projectId);

    const [sprintForm] = Form.useForm();
    const [sprintModalMode, setSprintModalMode] = useState('create');
    const [editingSprintId, setEditingSprintId] = useState(null);
    const [taskModalOpen, setTaskModalOpen] = useState(false);
    const [taskModalMode, setTaskModalMode] = useState('create');
    const [editingTask, setEditingTask] = useState(null);
    const [selectedSprintIdForTask, setSelectedSprintIdForTask] = useState(null);
    const [taskForm] = Form.useForm();
    const [sprintPendingAttachments, setSprintPendingAttachments] = useState([]);
    const [sprintExistingAttachments, setSprintExistingAttachments] = useState([]);
    const [taskPendingAttachments, setTaskPendingAttachments] = useState([]);
    const [pendingLinkAttachments, setPendingLinkAttachments] = useState([]);
    const [linkModalOpen, setLinkModalOpen] = useState(false);
    const [linkModalForm] = Form.useForm();
    const linkModalCallbackRef = useRef(null);
    const previousSprintMarkdownRef = useRef('');
    const sprintDraftKey = useMemo(() => `${SPRINT_CREATE_DRAFT_STORAGE_KEY}:${projectId ?? 'unknown'}`, [projectId]);
    const taskDraftKey = useMemo(() => `${TASK_CREATE_DRAFT_STORAGE_KEY}:${projectId ?? 'unknown'}`, [projectId]);

    const mentionUsers = useMemo(
        () => (project?.assignedUsers ?? []).slice(0, MENTION_USERS_LIMIT),
        [project?.assignedUsers],
    );
    const mentionMilestones = useMemo(() => (Array.isArray(milestones) ? milestones : []), [milestones]);
    const mentionSprints = useMemo(() => (Array.isArray(sprints) ? sprints : []), [sprints]);

    const makeRemovePending = useCallback((form, setPending) => {
        return (id) => {
            let nextDescription = null;
            setPending((prev) => {
                const index = prev.findIndex((p) => p.id === id);
                if (index < 0) return prev.filter((p) => p.id !== id);
                const removed = prev[index];
                if (removed?.preview) URL.revokeObjectURL(removed.preview);
                let content = form.getFieldValue('description') || '';
                const linkRegex = new RegExp(`<a\\s(?=[^>]*href="#attachment-${index}")[^>]*>[\\s\\S]*?</a>`, 'gi');
                content = content.replace(linkRegex, '');
                for (let j = prev.length - 1; j > index; j--) {
                    content = content.replace(new RegExp(`#attachment-${j}`, 'g'), `#attachment-${j - 1}`);
                }
                nextDescription = content;
                return prev.filter((p) => p.id !== id);
            });
            if (nextDescription != null) {
                form.setFieldValue('description', nextDescription);
            }
        };
    }, []);

    const handleSprintFilesSelected = useCallback((files) => {
        if (!files?.length) return;
        const next = files.map((file, i) => ({
            id: `sprint-${Date.now()}-${i}`,
            file,
            name: file.name,
            type: file.type,
            preview: (file.type || '').startsWith('image/') ? URL.createObjectURL(file) : null,
        }));
        setSprintPendingAttachments((prev) => [...prev, ...next]);
    }, []);
    const handleTaskFilesSelected = useCallback((files) => {
        if (!files?.length) return;
        const next = files.map((file, i) => ({
            id: `task-${Date.now()}-${i}`,
            file,
            name: file.name,
            type: file.type,
            preview: (file.type || '').startsWith('image/') ? URL.createObjectURL(file) : null,
        }));
        setTaskPendingAttachments((prev) => [...prev, ...next]);
    }, []);
    const removeSprintAttachment = useCallback(
        (id) => makeRemovePending(sprintForm, setSprintPendingAttachments)(id),
        [makeRemovePending, sprintForm],
    );
    const removeTaskAttachment = useCallback(
        (id) => makeRemovePending(taskForm, setTaskPendingAttachments)(id),
        [makeRemovePending, taskForm],
    );
    const stripFirstMatchingHrefAnchor = useCallback((html, href) => {
        if (!html || typeof html !== 'string' || !href) return html;
        const escaped = String(href).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const re = new RegExp(`<a\\s[^>]*href=["']${escaped}["'][^>]*>[\\s\\S]*?<\\/a>`, 'i');
        return html.replace(re, '');
    }, []);

    const collectAnchorHrefVariants = (html) => {
        const out = new Set();
        if (!html || typeof html !== 'string' || typeof document === 'undefined') return out;
        const root = document.createElement('div');
        root.innerHTML = html;
        root.querySelectorAll('a').forEach((a) => {
            const href = String(a.getAttribute('href') || '').trim();
            if (!href) return;
            out.add(href);
            const resolved = resolveAttachmentUrl(href);
            if (resolved) out.add(String(resolved));
        });
        return out;
    };

    const removeExistingSprintAttachment = useCallback(
        (idOrHref) => {
            if (!idOrHref) return;
            const key = String(idOrHref);
            setSprintExistingAttachments((prev) => {
                const match =
                    prev.find((x) => String(x.id) === key) ??
                    prev.find((x) => {
                        const raw = String(x.url || '');
                        const resolved = String(resolveAttachmentUrl(raw) || '');
                        return raw === key || resolved === key;
                    });
                const next = prev.filter((x) => String(x.id) !== key && String(x.url) !== key);
                const href = match?.url || (key.startsWith('http') ? key : null);
                if (href) {
                    const content = sprintForm.getFieldValue('description') || '';
                    const stripped = stripFirstMatchingHrefAnchor(content, href);
                    if (stripped !== content) {
                        sprintForm.setFieldValue('description', stripped);
                    }
                }
                return next.filter((x) => {
                    const raw = String(x.url || '');
                    const resolved = String(resolveAttachmentUrl(raw) || '');
                    return raw !== key && resolved !== key;
                });
            });
        },
        [sprintForm, stripFirstMatchingHrefAnchor],
    );

    const handleLinkRequest = useCallback(
        (insertLink) => {
            linkModalCallbackRef.current = insertLink;
            linkModalForm.resetFields();
            setLinkModalOpen(true);
        },
        [linkModalForm],
    );
    const handleLinkModalSubmit = useCallback(() => {
        linkModalForm.validateFields().then((values) => {
            const rawUrl = (values.url || '').trim();
            const title = (values.title || '').trim();
            const check = validateLinkUrlInput(rawUrl);
            if (!check.ok) {
                linkModalForm.setFields([{ name: 'url', errors: [check.message || 'Enter a valid URL'] }]);
                return;
            }
            const normalizedUrl = check.normalized;
            const insertLink = linkModalCallbackRef.current;
            const displayTitle = title || normalizedUrl;
            if (insertLink) insertLink({ url: normalizedUrl, title: displayTitle });
            setPendingLinkAttachments((prev) => [
                ...prev,
                { id: `link-${Date.now()}`, url: normalizedUrl, title: displayTitle },
            ]);
            setLinkModalOpen(false);
            linkModalCallbackRef.current = null;
        });
    }, [linkModalForm]);

    const removePendingLinkAttachment = useCallback(
        (idOrHref) => {
            if (!idOrHref) return;
            const key = String(idOrHref);
            setPendingLinkAttachments((prev) => {
                const match =
                    prev.find((x) => String(x.id) === key) ??
                    prev.find((x) => String(x.url) === key);
                const next = prev.filter((x) => String(x.id) !== key && String(x.url) !== key);

                const href = match?.url || (key.startsWith('http') ? key : null);
                if (href) {
                    const form = taskModalOpen ? taskForm : sprintForm;
                    const content = form.getFieldValue('description') || '';
                    const stripped = stripFirstMatchingHrefAnchor(content, href);
                    if (stripped !== content) {
                        form.setFieldValue('description', stripped);
                    }
                }
                return next;
            });
        },
        [sprintForm, taskForm, taskModalOpen, stripFirstMatchingHrefAnchor],
    );

    const handleEditorLinkRemoved = useCallback(
        (href) => {
            if (!href) return;
            removePendingLinkAttachment(String(href));
            removeExistingSprintAttachment(String(href));
        },
        [removePendingLinkAttachment, removeExistingSprintAttachment],
    );

    const watchedSprintDesc = Form.useWatch('description', sprintForm);
    const watchedTaskDesc = Form.useWatch('description', taskForm);
    const watchedSprintName = Form.useWatch('name', sprintForm);
    const watchedSprintStartDate = Form.useWatch('startDate', sprintForm);
    const watchedSprintEndDate = Form.useWatch('endDate', sprintForm);
    const watchedTaskTitle = Form.useWatch('title', taskForm);
    const watchedTaskPriority = Form.useWatch('priority', taskForm);
    const watchedTaskType = Form.useWatch('type', taskForm);
    const [hasSprintCreateDraft, setHasSprintCreateDraft] = useState(() => Boolean(getStorageValue(sprintDraftKey)));
    const [hasTaskCreateDraft, setHasTaskCreateDraft] = useState(() => Boolean(getStorageValue(taskDraftKey)));
    useEffect(() => {
        const html = taskModalOpen ? watchedTaskDesc : watchedSprintDesc;
        if (!html || typeof html !== 'string') return;
        setPendingLinkAttachments((prev) => prev.filter((x) => !x?.url || html.includes(String(x.url))));
    }, [watchedSprintDesc, watchedTaskDesc, taskModalOpen]);

    useEffect(() => {
        if (!createSprintModalOpen || sprintModalMode !== 'edit') return;
        const html = watchedSprintDesc;
        if (!html || typeof html !== 'string') return;
        const prevHtml = previousSprintMarkdownRef.current || '';
        const prevHrefs = collectAnchorHrefVariants(prevHtml);
        const currentHrefs = collectAnchorHrefVariants(html);
        setSprintExistingAttachments((prev) =>
            prev.filter((a) => {
                const url = String(a?.url || '');
                if (!url) return true;
                const resolvedUrl = String(resolveAttachmentUrl(url) || '');
                const wasPresent = prevHrefs.has(url) || (resolvedUrl ? prevHrefs.has(resolvedUrl) : false);
                const isPresent = currentHrefs.has(url) || (resolvedUrl ? currentHrefs.has(resolvedUrl) : false);
                // Only remove if user actually erased this anchor from markdown.
                if (wasPresent && !isPresent) return false;
                return true;
            }),
        );
        previousSprintMarkdownRef.current = html;
    }, [createSprintModalOpen, sprintModalMode, watchedSprintDesc]);

    useEffect(() => {
        if (!createSprintModalOpen || sprintModalMode !== 'edit') return;
        previousSprintMarkdownRef.current = typeof watchedSprintDesc === 'string' ? watchedSprintDesc : '';
    }, [createSprintModalOpen, sprintModalMode, editingSprintId, watchedSprintDesc]);

    useEffect(() => {
        const stored = getStorageValue(sprintDraftKey);
        if (!stored) return;
        try {
            const parsed = JSON.parse(stored);
            if (!parsed || parsed.mode !== 'create') return;
            sprintForm.setFieldsValue({
                name: parsed.name ?? '',
                startDate: parsed.startDate ? dayjs(parsed.startDate) : undefined,
                endDate: parsed.endDate ? dayjs(parsed.endDate) : undefined,
                description: parsed.description ?? '',
            });
            setPendingLinkAttachments(Array.isArray(parsed.pendingLinks) ? parsed.pendingLinks : []);
            setHasSprintCreateDraft(true);
        } catch (_err) {
            // ignore corrupted draft data
        }
    }, [sprintDraftKey, sprintForm]);

    useEffect(() => {
        const stored = getStorageValue(taskDraftKey);
        if (!stored) return;
        try {
            const parsed = JSON.parse(stored);
            if (!parsed || parsed.mode !== 'create') return;
            taskForm.setFieldsValue({
                title: parsed.title ?? '',
                priority: parsed.priority ?? 'medium',
                type: parsed.type ?? 'feature',
                description: parsed.description ?? '',
                assigneeId: parsed.assigneeId ?? undefined,
            });
            setSelectedSprintIdForTask(parsed.selectedSprintIdForTask ?? null);
            setPendingLinkAttachments(Array.isArray(parsed.pendingLinks) ? parsed.pendingLinks : []);
            setHasTaskCreateDraft(true);
        } catch (_err) {
            // ignore corrupted draft data
        }
    }, [taskDraftKey, taskForm]);

    const saveSprintDraft = useCallback(() => {
        if (sprintModalMode !== 'create') return;
        const values = sprintForm.getFieldsValue();
        const hasDraftContent = Boolean(
            (values?.name ?? '').trim() ||
            values?.description ||
            values?.startDate ||
            values?.endDate ||
            pendingLinkAttachments.length > 0,
        );
        if (!hasDraftContent) {
            removeStorageValue(sprintDraftKey);
            setHasSprintCreateDraft(false);
            return;
        }
        setStorageValue(
            sprintDraftKey,
            JSON.stringify({
                mode: 'create',
                name: values?.name ?? '',
                startDate: values?.startDate ? dayjs(values.startDate).format('YYYY-MM-DD') : null,
                endDate: values?.endDate ? dayjs(values.endDate).format('YYYY-MM-DD') : null,
                description: values?.description ?? '',
                pendingLinks: pendingLinkAttachments,
            }),
        );
        setHasSprintCreateDraft(true);
    }, [sprintForm, sprintModalMode, sprintDraftKey, pendingLinkAttachments]);

    const saveTaskDraft = useCallback(() => {
        if (taskModalMode !== 'create') return;
        const values = taskForm.getFieldsValue();
        const hasDraftContent = Boolean(
            (values?.title ?? '').trim() ||
            values?.description ||
            values?.assigneeId ||
            pendingLinkAttachments.length > 0,
        );
        if (!hasDraftContent) {
            removeStorageValue(taskDraftKey);
            setHasTaskCreateDraft(false);
            return;
        }
        setStorageValue(
            taskDraftKey,
            JSON.stringify({
                mode: 'create',
                selectedSprintIdForTask: selectedSprintIdForTask ?? null,
                title: values?.title ?? '',
                priority: values?.priority ?? 'medium',
                type: values?.type ?? 'feature',
                description: values?.description ?? '',
                assigneeId: values?.assigneeId ?? null,
                pendingLinks: pendingLinkAttachments,
            }),
        );
        setHasTaskCreateDraft(true);
    }, [taskForm, taskModalMode, taskDraftKey, selectedSprintIdForTask, pendingLinkAttachments]);

    const clearSprintCreateDraft = useCallback(() => {
        sprintForm.resetFields();
        setSprintPendingAttachments((prev) => {
            prev.forEach((p) => {
                if (p?.preview) URL.revokeObjectURL(p.preview);
            });
            return [];
        });
        setSprintExistingAttachments([]);
        setPendingLinkAttachments([]);
        removeStorageValue(sprintDraftKey);
        setHasSprintCreateDraft(false);
    }, [sprintForm, sprintDraftKey]);

    const clearTaskCreateDraft = useCallback(() => {
        taskForm.resetFields();
        setTaskPendingAttachments((prev) => {
            prev.forEach((p) => {
                if (p?.preview) URL.revokeObjectURL(p.preview);
            });
            return [];
        });
        setPendingLinkAttachments([]);
        setSelectedSprintIdForTask(null);
        removeStorageValue(taskDraftKey);
        setHasTaskCreateDraft(false);
    }, [taskForm, taskDraftKey]);

    useEffect(() => {
        const onBeforeUnload = () => {
            if (createSprintModalOpen && sprintModalMode === 'create') {
                saveSprintDraft();
            }
            if (taskModalOpen && taskModalMode === 'create') {
                saveTaskDraft();
            }
        };
        window.addEventListener('beforeunload', onBeforeUnload);
        return () => window.removeEventListener('beforeunload', onBeforeUnload);
    }, [
        createSprintModalOpen,
        sprintModalMode,
        saveSprintDraft,
        taskModalOpen,
        taskModalMode,
        saveTaskDraft,
    ]);

    useEffect(() => {
        if (!createSprintModalOpen || sprintModalMode !== 'create') return;
        saveSprintDraft();
    }, [
        createSprintModalOpen,
        sprintModalMode,
        watchedSprintName,
        watchedSprintDesc,
        watchedSprintStartDate,
        watchedSprintEndDate,
        pendingLinkAttachments,
        saveSprintDraft,
    ]);

    useEffect(() => {
        if (!taskModalOpen || taskModalMode !== 'create') return;
        saveTaskDraft();
    }, [
        taskModalOpen,
        taskModalMode,
        watchedTaskTitle,
        watchedTaskDesc,
        watchedTaskPriority,
        watchedTaskType,
        selectedSprintIdForTask,
        pendingLinkAttachments,
        taskForm,
        saveTaskDraft,
    ]);

    /** Watched in the hook (not in the modal UI) so Form.useWatch runs in a stable custom-hook context. */
    const sprintCreateStartDate = Form.useWatch('startDate', sprintForm);

    /**
     * Sprint date constraints:
     * - First sprint in first milestone: start from today.
     * - First sprint in later milestones: start after previous milestone due date.
     * - Next sprint in same milestone: start after latest sprint end date.
     * - End date cannot exceed milestone due date.
     */
    const sprintDateConstraints = useMemo(() => {
        const today = dayjs().startOf('day');
        const milestoneId =
            tabSelectedMilestoneId != null && tabSelectedMilestoneId !== '' ? String(tabSelectedMilestoneId) : null;
        const milestoneList = Array.isArray(milestones) ? milestones : [];
        let milestoneIndex = -1;
        let milestone = null;
        if (useMilestonesEnabled) {
            milestoneIndex =
                milestoneId != null ? milestoneList.findIndex((m) => String(m?.id) === milestoneId) : -1;
            milestone = milestoneIndex >= 0 ? milestoneList[milestoneIndex] : null;
        }

        // When milestones are disabled, don't apply milestone due-date constraints.
        const milestoneDue =
            useMilestonesEnabled && milestone?.dueDate && dayjs(milestone.dueDate).isValid()
                ? dayjs(milestone.dueDate).startOf('day')
                : null;

        const sprintList = Array.isArray(sprints) ? sprints : [];
        const phaseMilestones = useMilestonesEnabled
            ? []
            : milestoneList.filter((m) => String(m.phaseId) === milestoneId);
        const phaseMilestoneIds = phaseMilestones.map((m) => String(m.id));

        const scoped = milestoneId
            ? (useMilestonesEnabled
                ? sprintList.filter((sp) => String(sp?.milestoneId ?? sp?.milestone?.id ?? '') === milestoneId)
                : sprintList.filter((sp) => sp?.milestoneId && phaseMilestoneIds.includes(String(sp.milestoneId))))
            : sprintList;

        // Base start bound for create:
        // - today for first milestone
        // - max(today, previous milestone due + 1 day) for later milestones
        let baseStartBound = today;
        if (useMilestonesEnabled && milestoneIndex > 0) {
            const prevMilestone = milestoneList[milestoneIndex - 1];
            const prevDue =
                prevMilestone?.dueDate && dayjs(prevMilestone.dueDate).isValid()
                    ? dayjs(prevMilestone.dueDate).startOf('day').add(1, 'day')
                    : null;
            if (prevDue && prevDue.isAfter(baseStartBound, 'day')) {
                baseStartBound = prevDue;
            }
        }

        // Create flow only: apply calculated min start.
        // Edit keeps current behavior to avoid blocking historical records when editing name/details.
        let minStart = null;
        if (sprintModalMode === 'create') {
            const isRelaxing = project?.sprintDateRule !== 'strict' && project?.settings?.sprintDateRule !== 'strict';
            if (isRelaxing) {
                minStart = null;
            } else {
                let latestEnd = null;
                for (const sp of scoped) {
                    if (!sp?.endDate) continue;
                    const d = dayjs(sp.endDate);
                    if (!d.isValid()) continue;
                    const day = d.startOf('day');
                    if (!latestEnd || day.isAfter(latestEnd)) latestEnd = day;
                }
                if (latestEnd) {
                    const nextAfterLatest = latestEnd.add(1, 'day');
                    minStart = nextAfterLatest.isAfter(baseStartBound, 'day') ? nextAfterLatest : baseStartBound;
                } else {
                    minStart = baseStartBound;
                }
            }
        }

        return {
            minStartDate: minStart,
            maxEndDate: milestoneDue,
            milestoneDueDate: milestoneDue,
        };
    }, [tabSelectedMilestoneId, milestones, sprints, sprintModalMode, project, useMilestonesEnabled]);

    const closeSprintModal = useCallback(() => {
        if (sprintModalMode === 'create') {
            saveSprintDraft();
        }
        setCreateSprintModalOpen(false);
        if (sprintModalMode === 'edit') {
            sprintForm.resetFields();
            setSprintPendingAttachments((prev) => {
                prev.forEach((p) => {
                    if (p?.preview) URL.revokeObjectURL(p.preview);
                });
                return [];
            });
            setSprintExistingAttachments([]);
            setPendingLinkAttachments([]);
            setSprintModalMode('create');
            setEditingSprintId(null);
        }
    }, [saveSprintDraft, setCreateSprintModalOpen, sprintForm, sprintModalMode]);

    const cancelSprintModal = useCallback(() => {
        clearSprintCreateDraft();
        setCreateSprintModalOpen(false);
        setSprintModalMode('create');
        setEditingSprintId(null);
        setSprintExistingAttachments([]);
    }, [clearSprintCreateDraft, setCreateSprintModalOpen]);

    const openEditSprintModal = useCallback(
        (sprint) => {
            if (!sprint?.id) return;
            setSprintModalMode('edit');
            setEditingSprintId(sprint.id);
            setSprintPendingAttachments([]);
            const existingAttachments = getSprintAttachmentList(sprint).map((a, idx) => ({
                ...a,
                id: `${a.type || 'file'}-${idx}-${a.url || ''}`,
            }));
            setSprintExistingAttachments(existingAttachments);
            setPendingLinkAttachments([]);
            sprintForm.setFieldsValue({
                name: (sprint.name ?? '').trim() || '',
                startDate: sprint.startDate ? dayjs(sprint.startDate) : undefined,
                endDate: sprint.endDate ? dayjs(sprint.endDate) : undefined,
                description: enrichDescriptionAnchorsForEditor(getSprintDescriptionText(sprint) || '', sprint),
            });
            setCreateSprintModalOpen(true);
        },
        [sprintForm, setCreateSprintModalOpen, enrichDescriptionAnchorsForEditor],
    );

    const openCreateSprintModal = useCallback(() => {
        setSprintModalMode('create');
        setEditingSprintId(null);
        setSprintExistingAttachments([]);
        const values = sprintForm.getFieldsValue();
        const hasDraft = Boolean(
            (values?.name ?? '').trim() ||
            values?.description ||
            values?.startDate ||
            values?.endDate ||
            pendingLinkAttachments.length > 0,
        );
        if (!hasDraft) {
            // Auto-pick computed minimum start date.
            const minStart = sprintDateConstraints?.minStartDate;
            const maxEnd = sprintDateConstraints?.maxEndDate;
            if (minStart && (!maxEnd || !minStart.isAfter(maxEnd, 'day'))) {
                sprintForm.setFieldValue('startDate', minStart);
                const currentEnd = sprintForm.getFieldValue('endDate');
                if (currentEnd && dayjs(currentEnd).isValid() && dayjs(currentEnd).isBefore(minStart, 'day')) {
                    sprintForm.setFieldValue('endDate', null);
                }
            }
        }
        setCreateSprintModalOpen(true);
    }, [sprintForm, setCreateSprintModalOpen, sprintDateConstraints, pendingLinkAttachments.length]);

    const openTaskModal = useCallback((sprintId) => {
        setTaskModalMode('create');
        setEditingTask(null);
        const stored = getStorageValue(taskDraftKey);
        let restored = false;
        if (stored) {
            try {
                const parsed = JSON.parse(stored);
                if (parsed && parsed.mode === 'create') {
                    taskForm.setFieldsValue({
                        title: parsed.title ?? '',
                        priority: parsed.priority ?? 'medium',
                        type: parsed.type ?? 'feature',
                        description: parsed.description ?? '',
                    });
                    setSelectedSprintIdForTask(parsed.selectedSprintIdForTask ?? sprintId);
                    setPendingLinkAttachments(Array.isArray(parsed.pendingLinks) ? parsed.pendingLinks : []);
                    setHasTaskCreateDraft(true);
                    restored = true;
                }
            } catch (_err) {
                // ignore corrupted draft data
            }
        }
        if (!restored) {
            taskForm.resetFields();
            setPendingLinkAttachments([]);
            setSelectedSprintIdForTask(sprintId);
            setHasTaskCreateDraft(false);
        }
        setTaskModalOpen(true);
    }, [taskForm, taskDraftKey]);

    const openTaskModalForEdit = useCallback(
        (sprintId, task) => {
            if (!sprintId || !task) return;
            setTaskModalMode('edit');
            setEditingTask(task);
            setSelectedSprintIdForTask(sprintId);
            taskForm.setFieldsValue({
                title: (task.title ?? task.name ?? '').trim() || '',
                description: getTaskDescriptionMarkdown(task) || '',
                priority: task.priority || 'medium',
                type: task.type || 'feature',
            });
            setTaskPendingAttachments([]);
            setPendingLinkAttachments([]);
            setTaskModalOpen(true);
        },
        [taskForm],
    );

    const closeTaskModal = useCallback(() => {
        if (taskModalMode === 'create') {
            saveTaskDraft();
        }
        setTaskModalOpen(false);
        if (taskModalMode === 'edit') {
            setSelectedSprintIdForTask(null);
            setTaskModalMode('create');
            setEditingTask(null);
            taskForm.resetFields();
            setTaskPendingAttachments((prev) => {
                prev.forEach((p) => {
                    if (p?.preview) URL.revokeObjectURL(p.preview);
                });
                return [];
            });
            setPendingLinkAttachments([]);
        }
    }, [saveTaskDraft, taskForm, taskModalMode]);

    const cancelTaskModal = useCallback(() => {
        clearTaskCreateDraft();
        setTaskModalOpen(false);
        setTaskModalMode('create');
        setEditingTask(null);
    }, [clearTaskCreateDraft]);

    const handleSprintModalSubmit = useCallback(() => {
        sprintForm.validateFields().then((values) => {
            const startDate = values.startDate?.format?.('YYYY-MM-DD') ?? values.startDate;
            const endDate = values.endDate?.format?.('YYYY-MM-DD') ?? values.endDate;
            const descriptionHtml = contentToApiMentions(values.description ?? '');
            const description = buildSprintDescription(descriptionHtml);

            if (sprintModalMode === 'edit' && editingSprintId) {
                const mergedAttachments = [
                    ...sprintExistingAttachments.map(({ type, url, title }) => ({ type, url, title })),
                    ...pendingLinkAttachments.map(({ url, title }) => ({ type: 'link', url, title })),
                ];
                updateSprint(
                    editingSprintId,
                    {
                        name: (values.name ?? '').trim() || 'Sprint',
                        startDate: startDate || undefined,
                        endDate: endDate || undefined,
                        ...(description && { description }),
                        ...(sprintPendingAttachments.length > 0 && { files: sprintPendingAttachments.map((p) => p.file) }),
                        ...(mergedAttachments.length > 0 && {
                            attachments: mergedAttachments,
                        }),
                    },
                    {
                        onSuccess: () => {
                            message.success('Sprint updated');
                            closeSprintModal();
                        },
                        onError: (err) =>
                            message.error(err?.response?.data?.message || err?.message || 'Failed to update sprint'),
                    },
                );
                return;
            }

            // Resolve milestoneId or phaseId based on project capabilities
            let milestoneId = null;
            let phaseId = null;
            if (useMilestonesEnabled) {
                milestoneId =
                    tabSelectedMilestoneId != null && tabSelectedMilestoneId !== ''
                        ? tabSelectedMilestoneId
                        : (Array.isArray(milestones) && milestones[0] ? milestones[0].id : null);
                if (milestoneId == null || milestoneId === '') {
                    message.error('Select a milestone on the board first.');
                    return;
                }
            } else if (usePhasesEnabled) {
                phaseId =
                    tabSelectedMilestoneId != null && tabSelectedMilestoneId !== ''
                        ? tabSelectedMilestoneId
                        : (Array.isArray(phases) && phases[0] ? phases[0].id : null);
                if (phaseId == null || phaseId === '') {
                    message.error('Select a phase on the board first.');
                    return;
                }
            }

            const hasDescription = description && Object.keys(description).length > 0;

            createSprint(
                {
                    name: (values.name ?? '').trim() || 'Sprint',
                    ...(milestoneId ? { milestoneId } : {}),
                    ...(phaseId ? { phaseId } : {}),
                    startDate: startDate || undefined,
                    endDate: endDate || undefined,
                    ...(hasDescription && { description }),
                    ...(sprintPendingAttachments.length > 0 && { files: sprintPendingAttachments.map((p) => p.file) }),
                    ...(pendingLinkAttachments.length > 0 && {
                        attachments: pendingLinkAttachments.map(({ url, title }) => ({ url, title })),
                    }),
                },
                {
                    onSuccess: () => {
                        message.success('Sprint created');
                        clearSprintCreateDraft();
                        closeSprintModal();
                    },
                    onError: (err) =>
                        message.error(err?.response?.data?.message || err?.message || 'Failed to create sprint'),
                },
            );
        });
    }, [
        sprintForm,
        sprintModalMode,
        editingSprintId,
        createSprint,
        updateSprint,
        closeSprintModal,
        clearSprintCreateDraft,
        sprintPendingAttachments,
        sprintExistingAttachments,
        pendingLinkAttachments,
        tabSelectedMilestoneId,
        milestones,
        useMilestonesEnabled,
        usePhasesEnabled,
        phases,
    ]);

    const handleTaskModalSubmit = useCallback(() => {
        if (!selectedSprintIdForTask) return;
        taskForm.validateFields().then((values) => {
            const descriptionHtml = contentToApiMentions(values.description ?? '');
            const description = buildTaskDescription(descriptionHtml);

            if (taskModalMode === 'edit' && editingTask) {
                const tid = getSprintTaskApiId(editingTask) ?? editingTask?.id;
                if (tid == null) return;
                updateTask(
                    selectedSprintIdForTask,
                    tid,
                    {
                        title: (values.title ?? '').trim() || 'Task',
                        ...(description && { description }),
                        type: values.type || editingTask.type || 'feature',
                        priority: values.priority || 'medium',
                        ...(taskPendingAttachments.length > 0 && {
                            files: taskPendingAttachments.map((p) => p.file),
                        }),
                        ...(pendingLinkAttachments.length > 0 && {
                            attachments: pendingLinkAttachments.map(({ url, title }) => ({ url, title })),
                        }),
                    },
                    {
                        onSuccess: () => {
                            message.success('Task updated');
                            closeTaskModal();
                        },
                        onError: (err) =>
                            message.error(err?.response?.data?.message || err?.message || 'Failed to update task'),
                    },
                );
                return;
            }

            const taskBody = {
                title: (values.title ?? '').trim() || 'Task',
                ...(description && { description }),
                type: values.type || 'feature',
                priority: values.priority || 'medium',
                ...(values.assigneeId && { assigneeId: values.assigneeId }),
                ...(taskPendingAttachments.length > 0 && { files: taskPendingAttachments.map((p) => p.file) }),
                ...(pendingLinkAttachments.length > 0 && {
                    attachments: pendingLinkAttachments.map(({ url, title }) => ({ url, title })),
                }),
            };

            const useSprintsEnabled = project?.capabilities?.useSprints !== false;
            if (useSprintsEnabled) {
                taskBody.sprintId = selectedSprintIdForTask;
            } else {
                if (useMilestonesEnabled && tabSelectedMilestoneId) {
                    taskBody.milestoneId = tabSelectedMilestoneId;
                } else if (usePhasesEnabled && tabSelectedMilestoneId) {
                    taskBody.phaseId = tabSelectedMilestoneId;
                }
            }

            createTask(
                taskBody,
                {
                    onSuccess: () => {
                        message.success('Task created');
                        clearTaskCreateDraft();
                        closeTaskModal();
                    },
                    onError: (err) =>
                        message.error(err?.response?.data?.message || err?.message || 'Failed to create task'),
                },
            );
        });
    }, [
        taskForm,
        taskModalMode,
        editingTask,
        selectedSprintIdForTask,
        createTask,
        updateTask,
        closeTaskModal,
        clearTaskCreateDraft,
        taskPendingAttachments,
        pendingLinkAttachments,
        project,
        useMilestonesEnabled,
        usePhasesEnabled,
        tabSelectedMilestoneId,
    ]);

    const sprintSubmitPending = sprintModalMode === 'edit' ? updateSprintPending : createSprintPending;
    const taskSubmitPending = taskModalMode === 'edit' ? updateTaskPending : createTaskPending;

    return {
        sprintForm,
        taskForm,
        linkModalForm,
        createSprintModalOpen,
        sprintModalMode,
        taskModalMode,
        closeSprintModal,
        cancelSprintModal,
        resetSprintCreateDraft: clearSprintCreateDraft,
        hasSprintCreateDraft,
        openCreateSprintModal,
        openEditSprintModal,
        taskModalOpen,
        closeTaskModal,
        cancelTaskModal,
        resetTaskCreateDraft: clearTaskCreateDraft,
        hasTaskCreateDraft,
        openTaskModal,
        openTaskModalForEdit,
        linkModalOpen,
        setLinkModalOpen,
        linkModalCallbackRef,
        mentionUsers,
        mentionMilestones,
        mentionSprints,
        sprintCreateStartDate,
        sprintDateConstraints,
        sprintPendingAttachments,
        sprintExistingAttachments,
        taskPendingAttachments,
        pendingLinkAttachments,
        removeExistingSprintAttachment,
        removePendingLinkAttachment,
        handleEditorLinkRemoved,
        handleSprintFilesSelected,
        handleTaskFilesSelected,
        removeSprintAttachment,
        removeTaskAttachment,
        handleLinkRequest,
        handleLinkModalSubmit,
        handleSprintModalSubmit,
        handleTaskModalSubmit,
        handleCreateSprintSubmit: handleSprintModalSubmit,
        handleCreateTaskSubmit: handleTaskModalSubmit,
        createSprintPending,
        updateSprintPending,
        sprintSubmitPending,
        createTaskPending,
        updateTaskPending,
        taskSubmitPending,
        truncateFileName,
    };
}

export const SPRINT_TASKS_QUERY_KEY = (projectId, sprintId) => ['projects', projectId, 'sprints', sprintId, 'tasks'];

export function useSprintTasks(projectId, sprintId) {
    const can = useCanCheck();
    const canView = can(PROJECT_SPRINTS_VIEW_ABILITY);
    const { data: project } = useProjectDetail(projectId, { enabled: !!projectId });
    const useSprintsEnabled = project?.capabilities?.useSprints !== false;
    const {
        data: tasks = [],
        isLoading,
        isError,
        refetch,
    } = useQuery({
        queryKey: sprintId && sprintId !== 'no-sprint' ? SPRINT_TASKS_QUERY_KEY(projectId, sprintId) : ['projects', projectId, 'tasks'],
        queryFn: () => sprintId && sprintId !== 'no-sprint' ? getProjectSprintTasks(projectId, sprintId) : getProjectTasks(projectId),
        enabled: !!projectId && canView && (useSprintsEnabled ? !!sprintId : true),
    });

    return {
        tasks: Array.isArray(tasks) ? tasks : [],
        isLoading,
        isError,
        refetch,
        canView,
    };
}

export const PROJECT_SPRINTS_QUERY_KEY = (projectId) => ['projects', projectId, 'sprints'];

export const PROJECT_SPRINT_DETAIL_QUERY_KEY = (projectId, sprintId) => [
    'projects',
    projectId,
    'sprints',
    sprintId,
    'detail',
];

/**
 * @param {string} projectId
 * @param {{ enabled?: boolean }} options - When enabled is false, the sprints API is not called (e.g. when another tab is active).
 */
export function useProjectSprints(projectId, options = {}) {
    const queryClient = useQueryClient();
    const can = useCanCheck();
    const canView = can(PROJECT_SPRINTS_VIEW_ABILITY);

    const isQueryEnabled = options.enabled !== false && !!projectId && canView;
    const { data: project } = useProjectDetail(projectId, { enabled: isQueryEnabled && !options.project });
    const activeProject = options.project ?? project;
    const useSprintsEnabled = activeProject?.capabilities?.useSprints !== false;
    const enabled = isQueryEnabled && useSprintsEnabled;

    const {
        data: sprints = [],
        isLoading,
        isError,
        refetch,
    } = useQuery({
        queryKey: PROJECT_SPRINTS_QUERY_KEY(projectId),
        queryFn: () => getProjectSprints(projectId),
        enabled,
    });

    const createMutation = useMutation({
        mutationFn: (body) => {
            const files = normalizeBinaryFiles(body.files);
            const linkAttachments = normalizeLinkAttachments(body.attachments);
            if (files?.length) {
                const formData = new FormData();
                formData.append('name', typeof body.name === 'string' ? body.name : '');
                if (body.milestoneId) formData.append('milestoneId', body.milestoneId);
                if (body.phaseId) formData.append('phaseId', body.phaseId);
                if (body.startDate) formData.append('startDate', body.startDate);
                if (body.endDate) formData.append('endDate', body.endDate);
                if (body.description) formData.append('description', JSON.stringify(body.description));
                if (linkAttachments.length > 0) formData.append('attachmentUrls', JSON.stringify(linkAttachments));
                files.forEach((file) => formData.append('files', file));
                return createProjectSprint(projectId, formData);
            }
            const { files: _f, attachments: _a, ...rest } = body;
            return createProjectSprint(projectId, { ...rest, ...(linkAttachments.length > 0 && { attachments: linkAttachments }) });
        },
        onSuccess: () => {
            invalidateProjectQueries(queryClient, projectId);
        },
    });

    const updateSprintMutation = useMutation({
        mutationFn: ({ sprintId, body }) => {
            const files = normalizeBinaryFiles(body.files);
            const forceMultipart = hasAnyFileTypeAttachment(body.attachments);
            const linkAttachments = normalizeLinkAttachments(body.attachments);
            if (files?.length || forceMultipart) {
                const formData = new FormData();
                formData.append('name', typeof body.name === 'string' ? body.name : '');
                if (body.startDate) formData.append('startDate', body.startDate);
                if (body.endDate) formData.append('endDate', body.endDate);
                if (body.description) formData.append('description', JSON.stringify(body.description));
                if (linkAttachments.length > 0) formData.append('attachmentUrls', JSON.stringify(linkAttachments));
                files.forEach((file) => formData.append('files', file));
                return updateProjectSprint(projectId, sprintId, formData);
            }
            const { files: _f, attachments: _a, ...rest } = body;
            return updateProjectSprint(projectId, sprintId, {
                ...rest,
                ...(linkAttachments.length > 0 && { attachments: linkAttachments }),
            });
        },
        onSuccess: () => {
            invalidateProjectQueries(queryClient, projectId);
        },
    });

    const deleteSprintMutation = useMutation({
        mutationFn: (sprintId) => deleteProjectSprint(projectId, sprintId),
        onSuccess: () => {
            invalidateProjectQueries(queryClient, projectId);
        },
    });

    const createSprint = (body, callbacks = {}) => {
        createMutation.mutate(body, {
            onSuccess: callbacks.onSuccess,
            onError: callbacks.onError,
        });
    };

    const updateSprint = (sprintId, body, callbacks = {}) => {
        updateSprintMutation.mutate(
            { sprintId, body },
            {
                onSuccess: callbacks.onSuccess,
                onError: callbacks.onError,
            },
        );
    };

    const deleteSprint = (sprintId, callbacks = {}) => {
        deleteSprintMutation.mutate(sprintId, {
            onSuccess: callbacks.onSuccess,
            onError: callbacks.onError,
        });
    };

    return {
        sprints: Array.isArray(sprints) ? sprints : [],
        isLoading,
        isError,
        refetch,
        createSprint,
        createPending: createMutation.isPending,
        updateSprint,
        updateSprintPending: updateSprintMutation.isPending,
        deleteSprint,
        deleteSprintPending: deleteSprintMutation.isPending,
        canView,
    };
}

export const CREATE_SPRINT_DISABLED_TOOLTIP = 'Add at least one milestone or phase before creating a sprint.';
export const PROJECT_SPRINT_TASK_DETAIL_QUERY_KEY = (projectId, sprintId, taskId) => [
    'projects',
    projectId,
    'sprints',
    sprintId,
    'tasks',
    taskId,
    'detail',
];

export function extractTaskDetailPayload(raw, fallbackTask = null) {
    if (!raw || typeof raw !== 'object') {
        return {
            task: fallbackTask,
            bugs: Array.isArray(fallbackTask?.bugs) ? fallbackTask.bugs : [],
        };
    }
    const taskCandidate =
        (raw.task && typeof raw.task === 'object' ? raw.task : null) ??
        (raw.data && typeof raw.data === 'object' ? raw.data : null);
    const task = taskCandidate ?? raw;
    const bugs = Array.isArray(raw.bugs)
        ? raw.bugs
        : Array.isArray(task?.bugs)
            ? task.bugs
            : Array.isArray(fallbackTask?.bugs)
                ? fallbackTask.bugs
                : [];
    return { task, bugs };
}

/**
 * Project Sprints tab: data, RBAC, milestone navigation, Kanban, modals, QA submission.
 */
export function useProjectSprintsTab(projectId, options = {}) {
    const {
        project: initialProject,
        createSprintModalOpen: createSprintModalOpenProp,
        setCreateSprintModalOpen: setCreateSprintModalOpenProp,
    } = options;
    const queryClient = useQueryClient();

    const can = useCanCheck();
    const canViewSprints = can(PROJECT_SPRINTS_VIEW_ABILITY);
    const canCreateSprint = can(PROJECT_SPRINTS_CREATE_ABILITY);
    const canEditSprint = can(PROJECT_SPRINTS_UPDATE_ABILITY);
    const canDeleteSprint = can(PROJECT_SPRINTS_DELETE_ABILITY);
    const canCreateTask = can(PROJECT_TASKS_CREATE_ABILITY);
    const canEditTask = can(PROJECT_TASKS_UPDATE_ABILITY);
    const canDeleteTask = can(PROJECT_TASKS_DELETE_ABILITY);
    const canMarkTaskTodo = can(PROJECT_TASKS_MARK_TODO_ABILITY);
    const canAssignTask = can(PROJECT_TASKS_ASSIGN_ABILITY);

    const [createSprintModalOpenLocal, setCreateSprintModalOpenLocal] = useState(false);
    const createSprintModalOpen = createSprintModalOpenProp ?? createSprintModalOpenLocal;
    const setCreateSprintModalOpen = setCreateSprintModalOpenProp ?? setCreateSprintModalOpenLocal;

    const project = initialProject;

    const [selectedMilestoneId, setSelectedMilestoneId] = useState(null);

    const useMilestonesEnabled = project ? project.capabilities?.useMilestones !== false : false;
    const { data: milestonesData = [] } = useQuery({
        queryKey: ['projects', projectId, 'milestones'],
        queryFn: () => getProjectMilestones(projectId),
        enabled: Boolean(projectId && useMilestonesEnabled),
        staleTime: 0,
    });
    const milestones = useMemo(() => (useMilestonesEnabled ? milestonesData : []), [useMilestonesEnabled, milestonesData]);

    const currentUserId = useAuthStore((s) => s.user?.id ?? null);

    const taskAssignPicklistUsers = useMemo(() => {
        const list = Array.isArray(project?.assignedUsers) ? project.assignedUsers : [];
        const others =
            currentUserId == null ? list : list.filter((u) => u && String(u.id) !== String(currentUserId));
        return others.slice(0, MENTION_USERS_LIMIT);
    }, [project?.assignedUsers, currentUserId]);

    // When milestones are enabled, sprints are nested under milestones in the project payload.
    // When milestones are disabled (phase-only mode), fetch sprints from the dedicated API.
    const sprintsFromMilestones = useMemo(() => {
        const out = [];
        for (const m of milestones) {
            const list = Array.isArray(m?.sprints) ? m.sprints : [];
            for (const s of list) out.push(s);
        }
        return out;
    }, [milestones]);

    const useSprintsEnabled = project?.capabilities?.useSprints !== false;
    const usePhasesEnabled = project ? project.capabilities?.usePhases !== false : false;

    // Fetch phases for tab display and automated milestone linking
    const { data: phasesData = [] } = useQuery({
        queryKey: ['projects', projectId, 'phases'],
        queryFn: () => getProjectPhases(projectId),
        enabled: Boolean(projectId && usePhasesEnabled && !useMilestonesEnabled),
        staleTime: 0,
    });
    const phases = useMemo(() => (Array.isArray(phasesData) ? phasesData : []), [phasesData]);

    const hasMilestones = useMemo(() => {
        if (useMilestonesEnabled) {
            return milestones.length > 0;
        }
        if (usePhasesEnabled) {
            return phases.length > 0;
        }
        return true;
    }, [useMilestonesEnabled, milestones, usePhasesEnabled, phases]);

    // NOTE: Dummy milestone auto-creation has been intentionally removed.
    // Phase-only projects (useMilestones=false, usePhases=true) associate sprints
    // with phases via description.phaseId — no milestone foreign key needed.

    // Fetch sprints from dedicated API when milestones are disabled
    // Fetch sprints conditionally based on project config and tab selection
    const sprintsQueryKey = useMilestonesEnabled
        ? ['projects', projectId, 'milestones', selectedMilestoneId, 'sprints']
        : (usePhasesEnabled
            ? ['projects', projectId, 'phases', selectedMilestoneId, 'sprints']
            : ['projects', projectId, 'sprints', 'standalone']);

    const sprintsQueryFn = () => {
        if (useMilestonesEnabled) {
            return getProjectSprintsForMilestone(projectId, selectedMilestoneId);
        }
        if (usePhasesEnabled) {
            return getProjectSprintsForPhase(projectId, selectedMilestoneId);
        }
        return getProjectSprintsStandalone(projectId);
    };

    const isSprintsQueryEnabled = Boolean(
        projectId &&
        useSprintsEnabled &&
        (useMilestonesEnabled ? !!selectedMilestoneId : (usePhasesEnabled ? !!selectedMilestoneId : true))
    );

    const {
        data: fetchedSprintsData,
        isFetching: isSprintQueryFetching,
        isLoading: isSprintQueryLoading,
    } = useQuery({
        queryKey: sprintsQueryKey,
        queryFn: sprintsQueryFn,
        enabled: isSprintsQueryEnabled,
        staleTime: 0,
    });

    const sprints = useMemo(() => {
        return Array.isArray(fetchedSprintsData) ? fetchedSprintsData : [];
    }, [fetchedSprintsData]);

    const sprintTabSelection = useProjectSprintTabSelection(
        useMilestonesEnabled ? milestones : (phases.length > 0 ? phases : []),
        sprints,
        {
            useMilestonesEnabled,
            projectMilestones: milestones,
            selectedMilestoneId,
            setSelectedMilestoneId,
        }
    );

    // True when there are real sprints in the project (across all milestones).
    // Computed here (before queries) so it can gate the projectTasks query.
    const hasSprints = sprints.length > 0;

    const EMPTY_TASKS = useMemo(() => [], []);

    const {
        data: projectTasks = EMPTY_TASKS,
        isFetching: projectTasksFetching,
        isPending: projectTasksPending,
    } = useQuery({
        queryKey: ['projects', projectId, 'tasks', sprintTabSelection.selectedMilestoneId ?? null],
        queryFn: () => {
            if (useMilestonesEnabled && sprintTabSelection.selectedMilestoneId) {
                return getProjectTasks(projectId, { milestoneId: sprintTabSelection.selectedMilestoneId });
            }
            if (usePhasesEnabled && sprintTabSelection.selectedMilestoneId) {
                return getProjectTasks(projectId, { phaseId: sprintTabSelection.selectedMilestoneId });
            }
            return getProjectTasks(projectId);
        },
        // Fetch project-wide tasks ONLY when sprints are disabled.
        // When milestones/phases are enabled, wait for selection before fetching
        // so we always hit the scoped endpoint — never all tasks.
        enabled: Boolean(projectId && !useSprintsEnabled) && (
            useMilestonesEnabled ? !!sprintTabSelection.selectedMilestoneId
            : usePhasesEnabled ? !!sprintTabSelection.selectedMilestoneId
            : true
        ),
    });

    const {
        data: sprintDetailData,
        isFetching: sprintDetailFetching,
        isPending: sprintDetailPending,
    } = useQuery({
        queryKey: PROJECT_SPRINT_DETAIL_QUERY_KEY(projectId, sprintTabSelection.selectedSprintIdResolved),
        queryFn: () => getProjectSprint(projectId, sprintTabSelection.selectedSprintIdResolved),
        enabled: Boolean(projectId && sprintTabSelection.selectedSprintIdResolved && canViewSprints && useSprintsEnabled),
    });

    const sprintsMergedFromDetail = useMemo(
        () => mergeSprintDetailIntoSprints(sprints, sprintDetailData, sprintTabSelection.selectedSprintIdResolved),
        [sprints, sprintDetailData, sprintTabSelection.selectedSprintIdResolved],
    );

    const submissionsForSelectedSprint = useMemo(
        () => normalizeProjectSprintDetailPayload(sprintDetailData).submissions,
        [sprintDetailData],
    );

    /**
     * Resolve stored `{{user:id}}` placeholders into display links using `descriptionMentions`,
     * so Sprint Details + task modals render names instead of raw ids.
     */
    const sprintsEnrichedForDisplay = useMemo(() => {
        // When sprints capability is off, fall back to a virtual "no-sprint" entry so the
        // task kanban is always rendered in tasks-only mode.
        if (!useSprintsEnabled) {
            let filteredTasks = projectTasks;
            if (useMilestonesEnabled && sprintTabSelection.selectedMilestoneId) {
                filteredTasks = projectTasks.filter(
                    (t) => String(t.milestoneId) === String(sprintTabSelection.selectedMilestoneId)
                );
            } else if (usePhasesEnabled && sprintTabSelection.selectedMilestoneId) {
                filteredTasks = projectTasks.filter(
                    (t) => String(t.phaseId) === String(sprintTabSelection.selectedMilestoneId)
                );
            }
            return [{
                id: 'no-sprint',
                name: 'No Sprint',
                tasks: filteredTasks,
            }];
        }
        if (!sprintsMergedFromDetail.length) return sprintsMergedFromDetail;
        return sprintsMergedFromDetail.map((s) => {
            const resolvedSprintDesc = resolveDescriptionMentionsForDisplay(getSprintDescriptionText(s), s?.descriptionMentions);
            const sprintDescription =
                s?.description && typeof s.description === 'object'
                    ? { ...s.description, ...(typeof resolvedSprintDesc === 'string' ? { content: resolvedSprintDesc } : {}) }
                    : (typeof resolvedSprintDesc === 'string' ? resolvedSprintDesc : s?.description);

            const tasks = Array.isArray(s?.tasks) ? s.tasks : [];
            const nextTasks = tasks.map((t) => {
                const resolvedTaskMd = resolveDescriptionMentionsForDisplay(getTaskDescriptionMarkdown(t), t?.descriptionMentions);
                const taskDescription =
                    t?.description && typeof t.description === 'object'
                        ? { ...t.description, ...(typeof resolvedTaskMd === 'string' ? { markdown: resolvedTaskMd } : {}) }
                        : (typeof resolvedTaskMd === 'string' ? { markdown: resolvedTaskMd } : t?.description);
                return {
                    ...t,
                    ...(taskDescription !== undefined ? { description: taskDescription } : {}),
                };
            });

            return {
                ...s,
                ...(sprintDescription !== undefined ? { description: sprintDescription } : {}),
                tasks: nextTasks,
            };
        });
    }, [
        sprintsMergedFromDetail,
        useSprintsEnabled,
        projectTasks,
        useMilestonesEnabled,
        usePhasesEnabled,
        sprintTabSelection.selectedMilestoneId,
    ]);

    const [, setSearchParams] = useSearchParams();
    const goToMilestonesTab = useCallback(() => {
        setSearchParams({ tab: useMilestonesEnabled ? 'milestones' : 'phases' });
    }, [setSearchParams, useMilestonesEnabled]);

    // Mutations only: POST /projects/:id/sprints — no GET /projects/:id/sprints list fetch.
    const {
        createSprint,
        createPending: createSprintPending,
        updateSprint,
        updatePending: updateSprintPending,
        deleteSprint,
        deleteSprintPending,
    } = useProjectSprints(projectId, { enabled: false, project });
    const {
        completeTask,
        revertTaskToTodo,
        updateTask,
        updateTaskPending,
        deleteTask,
        deleteTaskPending,
        sortTask,
        assignTask,
        assignTaskPending,
    } = useUpdateSprintTask(projectId);

    const {
        submissionPanelOpen,
        closeSubmissionPanel,
        submissionSession,
        submittedItems,
        submissionNotes,
        setSubmissionNotes,
        submissionPendingFiles,
        submissionPendingLinks,
        submissionLinkModalOpen,
        submissionLinkForm,
        handleSubmissionFilesSelected,
        handleSubmissionLinkRequest,
        handleSubmissionLinkModalSubmit,
        closeSubmissionLinkModal,
        removeSubmissionPendingFile,
        removeSubmissionPendingLink,
        handleSubmissionEditorLinkRemoved,
        submitToQA,
        submitToQAPending,
        removeFromSubmission,
        addToSubmission,
        addTasksToSubmissionBatch,
        openSubmissionPanel,
        submissionGroupedBySprintEnriched,
        isQAEnabled,
        isPMOEnabled,
        submittedTo,
        setSubmittedTo,
    } = useProjectSprintSubmission(projectId, { isSprintsTabActive: true, sprints: sprintsEnrichedForDisplay, project });

    const consultation = useProjectTaskConsultation(projectId);
    const { addToConsultation, openConsultationModal } = consultation;

    const handleRequestConsultation = useCallback(
        ({ sprintId, taskId, task }) => {
            const tid = getSprintTaskApiId(task) ?? taskId;
            const fullSprint = sprintsEnrichedForDisplay.find((s) => String(s.id) === String(sprintId));
            addToConsultation({
                type: 'task',
                id: tid,
                name: task?.title ?? task?.name ?? 'Task',
                sprintId,
                fullSprint,
                fullTask: task,
            });
            openConsultationModal();
        },
        [addToConsultation, openConsultationModal, sprintsEnrichedForDisplay],
    );

    const handleRequestSubmitToQA = useCallback(
        ({ sprintId, taskId, task, selectedTaskIds }) => {
            if (Array.isArray(selectedTaskIds) && selectedTaskIds.length > 0) {
                const sprint = sprintsEnrichedForDisplay.find((s) => String(s.id) === String(sprintId));
                const selectedTasks = (sprint?.tasks || []).filter((t) => {
                    const tid = getSprintTaskApiId(t) ?? t?.id ?? t?.taskId;
                    return selectedTaskIds.map(String).includes(String(tid));
                });
                const entries = selectedTasks.map((t) => ({
                    type: 'task',
                    id: getSprintTaskApiId(t) ?? t?.id ?? t?.taskId,
                    name: t?.title ?? t?.name ?? 'Task',
                    sprintId,
                })).filter(e => e.id != null);
                
                if (entries.length > 0) {
                    addTasksToSubmissionBatch(entries);
                    openSubmissionPanel();
                    message.success(`Added ${entries.length} task(s) to submission`);
                }
            } else {
                const tid = getSprintTaskApiId(task) ?? taskId;
                addToSubmission({
                    type: 'task',
                    id: tid,
                    name: task?.title ?? task?.name ?? 'Task',
                    sprintId,
                });
                openSubmissionPanel();
            }
        },
        [addToSubmission, openSubmissionPanel, sprintsEnrichedForDisplay, addTasksToSubmissionBatch],
    );

    // When there are no real sprints, the virtual 'no-sprint' entry has no milestoneId,
    // so the kanban's milestone filter would exclude it. Pass empty milestones to bypass
    // the filter, and force-select 'no-sprint' so the board always renders in tasks mode.
    const noSprintsMode = !useSprintsEnabled || !hasSprints;

    const kanban = useProjectSprintsKanbanBoard({
        milestones: noSprintsMode ? [] : (useMilestonesEnabled ? milestones : (phases.length > 0 ? phases : [])),
        sprints: sprintsEnrichedForDisplay,
        selectedMilestoneId: sprintTabSelection.selectedMilestoneId,
        setSelectedMilestoneId: sprintTabSelection.setSelectedMilestoneId,
        selectedSprintId: noSprintsMode ? 'no-sprint' : sprintTabSelection.selectedSprintIdResolved,
        setSelectedSprintId: sprintTabSelection.setSelectedSprintId,
        completeTask,
        revertTaskToTodo,
        updateTask,
        sortTask,
        onRequestSubmitToQA: handleRequestSubmitToQA,
        onRequestConsultation: handleRequestConsultation,
        canMarkTodo: canMarkTaskTodo,
        canReorderTodo: canEditTask,
        useMilestonesEnabled,
        projectMilestones: milestones,
    });

    const selectedSprintMerged = useMemo(
        () =>
            sprintsMergedFromDetail.find((s) => String(s.id) === String(sprintTabSelection.selectedSprintIdResolved)) ??
            null,
        [sprintsMergedFromDetail, sprintTabSelection.selectedSprintIdResolved],
    );

    const { groups: sprintSubmittedSubmissionGroups, orphanTasks: sprintSubmittedOrphanTasks } = useMemo(
        () =>
            buildSprintSubmittedColumnSubmissionLayout({
                sprintId: sprintTabSelection.selectedSprintIdResolved ?? null,
                submittedTasks: partitionSprintTasks(selectedSprintMerged?.tasks).submitted,
                qaSubmissions: submissionsForSelectedSprint,
            }),
        [sprintTabSelection.selectedSprintIdResolved, selectedSprintMerged?.tasks, submissionsForSelectedSprint],
    );

    const modals = useProjectSprintsTabModals(projectId, {
        project,
        milestones,
        sprints: sprintsEnrichedForDisplay,
        createSprint,
        createSprintPending,
        updateSprint,
        updateSprintPending,
        createSprintModalOpen,
        setCreateSprintModalOpen,
        tabSelectedMilestoneId: sprintTabSelection.selectedMilestoneId,
        updateTask,
        updateTaskPending,
        useMilestonesEnabled,
        usePhasesEnabled,
        phases,
    });

    const { openTaskModalForEdit } = modals;

    const [sprintTaskDetail, setSprintTaskDetail] = useState(null);
    const [sprintTaskDetailBugs, setSprintTaskDetailBugs] = useState([]);
    const [sprintTaskDetailLoading, setSprintTaskDetailLoading] = useState(false);
    const [taskForAssignModal, setTaskForAssignModal] = useState(null);

    useEffect(() => {
        setSprintTaskDetail(null);
        setSprintTaskDetailBugs([]);
        setSprintTaskDetailLoading(false);
        setTaskForAssignModal(null);
    }, [sprintTabSelection.selectedSprintIdResolved]);

    const closeSprintTaskDetail = useCallback(() => {
        setSprintTaskDetail(null);
        setSprintTaskDetailBugs([]);
        setSprintTaskDetailLoading(false);
    }, []);

    const openSprintTaskDetail = useCallback(async (a, b) => {
        const sprint = b != null ? a : kanban.selectedSprint;
        const task = b != null ? b : a;
        if (!task || !sprint?.id || !projectId) return;
        const taskId = getSprintTaskApiId(task) ?? task?.id ?? task?.taskId;
        if (!taskId) return;

        setSprintTaskDetail({ sprint, task, mode: 'view' });
        setSprintTaskDetailBugs(Array.isArray(task?.bugs) ? task.bugs : []);
        setSprintTaskDetailLoading(true);

        try {
            const detail = await queryClient.fetchQuery({
                queryKey: PROJECT_SPRINT_TASK_DETAIL_QUERY_KEY(projectId, sprint.id, taskId),
                queryFn: () => getProjectSprintTaskDetail(projectId, sprint.id, taskId),
                staleTime: 60 * 1000,
            });
            const { task: fullTask, bugs } = extractTaskDetailPayload(detail, task);
            setSprintTaskDetail({ sprint, task: fullTask, mode: 'view' });
            setSprintTaskDetailBugs(Array.isArray(bugs) ? bugs : []);
        } catch (err) {
            message.error(err?.response?.data?.message || err?.message || 'Failed to load task details');
        } finally {
            setSprintTaskDetailLoading(false);
        }
    }, [kanban.selectedSprint, projectId, queryClient]);

    const refreshSprintTaskDetail = useCallback(async () => {
        const sprint = sprintTaskDetail?.sprint;
        const task = sprintTaskDetail?.task;
        if (!sprint?.id || !task) return;
        await openSprintTaskDetail(sprint, task);
    }, [openSprintTaskDetail, sprintTaskDetail?.sprint, sprintTaskDetail?.task]);

    const openSprintTaskForEdit = useCallback(
        (task) => {
            const sprint = kanban.selectedSprint;
            if (!task || !sprint?.id) return;
            setSprintTaskDetail(null);
            openTaskModalForEdit(sprint.id, task);
        },
        [kanban.selectedSprint, openTaskModalForEdit],
    );

    const openTaskAssignModal = useCallback((task) => {
        setTaskForAssignModal(task);
    }, []);

    const closeTaskAssignModal = useCallback(() => {
        setTaskForAssignModal(null);
    }, []);

    const submitTaskAssign = useCallback(
        (assigneeId) => {
            const sprintId = kanban.selectedSprint?.id;
            const task = taskForAssignModal;
            const taskId = task ? getSprintTaskApiId(task) ?? task?.id ?? task?.taskId : null;
            if (!sprintId || taskId == null || !assigneeId) return;
            assignTask(sprintId, taskId, assigneeId, {
                onSuccess: () => {
                    message.success('User assigned to task');
                    closeTaskAssignModal();
                },
                onError: (err) =>
                    message.error(err?.response?.data?.message || err?.message || 'Failed to assign user'),
            });
        },
        [assignTask, closeTaskAssignModal, kanban.selectedSprint?.id, taskForAssignModal],
    );

    const handleDeleteSprintTask = useCallback(
        (task) => {
            const tid = getSprintTaskApiId(task) ?? task?.id;
            const sprintId = kanban.selectedSprint?.id;
            if (tid == null || sprintId == null) return;
            Modal.confirm({
                title: 'Delete this task?',
                content: 'This cannot be undone.',
                okText: 'Yes, delete',
                cancelText: 'No',
                okType: 'danger',
                onOk: () =>
                    new Promise((resolve, reject) => {
                        deleteTask(sprintId, tid, {
                            onSuccess: () => {
                                message.success('Task deleted');
                                closeSprintTaskDetail();
                                resolve();
                            },
                            onError: (err) => {
                                message.error(
                                    err?.response?.data?.message || err?.message || 'Failed to delete task',
                                );
                                reject(err);
                            },
                        });
                    }),
            });
        },
        [deleteTask, closeSprintTaskDetail, kanban.selectedSprint],
    );

    const handleDeleteSprint = useCallback(
        (sprint) => {
            const sid = sprint?.id ?? kanban.selectedSprint?.id;
            if (!sid) return;
            Modal.confirm({
                title: 'Delete this sprint?',
                content: 'This cannot be undone. Tasks in this sprint will also be removed.',
                okText: 'Yes, delete',
                cancelText: 'No',
                okType: 'danger',
                onOk: () =>
                    new Promise((resolve, reject) => {
                        deleteSprint(sid, {
                            onSuccess: () => {
                                message.success('Sprint deleted');
                                resolve();
                            },
                            onError: (err) => {
                                message.error(
                                    err?.response?.data?.message || err?.message || 'Failed to delete sprint',
                                );
                                reject(err);
                            },
                        });
                    }),
            });
        },
        [deleteSprint, kanban.selectedSprint],
    );

    const sprintTaskDetailMarkdown = useMemo(() => {
        const t = sprintTaskDetail?.task;
        return t ? getTaskDescriptionMarkdown(t) : '';
    }, [sprintTaskDetail?.task]);

    const [celebratingTaskKey, setCelebratingTaskKey] = useState(null);

    const handleMarkTaskDone = useCallback(
        (sprintId, taskId) => {
            if (!canMarkTaskTodo) {
                message.warning("You don't have permission to mark task todo/completed.");
                return;
            }
            if (!sprintId || !taskId) return;
            completeTask(sprintId, taskId, {
                onSuccess: () => {
                    message.success('Task marked as done');
                    setCelebratingTaskKey(`${sprintId}_${taskId}`);
                },
                onError: (err) =>
                    message.error(err?.response?.data?.message || err?.message || 'Failed to update task'),
            });
        },
        [completeTask, canMarkTaskTodo],
    );

    const handleRevertToTodo = useCallback(
        (sprintId, taskId) => {
            if (!canMarkTaskTodo) {
                message.warning("You don't have permission to mark task todo/completed.");
                return;
            }
            if (!sprintId || !taskId) return;
            revertTaskToTodo(sprintId, taskId, {
                onSuccess: () => message.success('Task reverted to todo'),
                onError: (err) =>
                    message.error(err?.response?.data?.message || err?.message || 'Failed to revert task'),
            });
        },
        [revertTaskToTodo, canMarkTaskTodo],
    );

    /**
     * Todo / Completed header dropdown: run real APIs (bulk complete, bulk revert, or add tasks to QA submission panel).
     * @param {'todo'|'completed'} sourceColumn
     * @param {'todo'|'completed'|'submitted'} targetKey
     */
    const handleColumnFlowAction = useCallback(
        (sourceColumn, targetKey) => {
            const sprint = kanban.selectedSprint;
            if (!sprint?.id) return;
            const sprintId = sprint.id;
            const { todo, completed } = partitionSprintTasks(sprint.tasks);

            const taskEntriesFrom = (tasks) =>
                tasks
                    .map((task) => ({
                        type: 'task',
                        id: getSprintTaskApiId(task) ?? task?.id ?? task?.taskId,
                        name: task?.title ?? task?.name ?? 'Task',
                        sprintId,
                    }))
                    .filter((e) => e.id != null);

            if (sourceColumn === 'todo') {
                if (targetKey === 'completed') {
                    if (!canMarkTaskTodo) {
                        message.warning("You don't have permission to mark task todo/completed.");
                        return;
                    }
                    if (todo.length === 0) {
                        message.info('No tasks in Todo');
                        return;
                    }
                    void (async () => {
                        try {
                            for (const task of todo) {
                                const taskId = getSprintTaskApiId(task) ?? task?.id ?? task?.taskId;
                                if (!taskId) continue;
                                await new Promise((resolve, reject) => {
                                    completeTask(sprintId, taskId, { onSuccess: resolve, onError: reject });
                                });
                            }
                            message.success(`Marked ${todo.length} task(s) as completed`);
                        } catch (err) {
                            message.error(
                                err?.response?.data?.message || err?.message || 'Failed to complete tasks',
                            );
                        }
                    })();
                    return;
                }
                if (targetKey === 'submitted') {
                    if (todo.length === 0) {
                        message.info('No tasks in Todo');
                        return;
                    }
                    const entries = taskEntriesFrom(todo);
                    if (entries.length === 0) {
                        message.info('No valid tasks to add');
                        return;
                    }
                    addTasksToSubmissionBatch(entries);
                    openSubmissionPanel();
                    message.success(`Added ${entries.length} task(s) to submission`);
                }
                return;
            }

            if (sourceColumn === 'completed') {
                if (targetKey === 'todo') {
                    if (!canMarkTaskTodo) {
                        message.warning("You don't have permission to mark task todo/completed.");
                        return;
                    }
                    if (completed.length === 0) {
                        message.info('No tasks in Completed');
                        return;
                    }
                    void (async () => {
                        try {
                            for (const task of completed) {
                                const taskId = getSprintTaskApiId(task) ?? task?.id ?? task?.taskId;
                                if (!taskId) continue;
                                await new Promise((resolve, reject) => {
                                    revertTaskToTodo(sprintId, taskId, { onSuccess: resolve, onError: reject });
                                });
                            }
                            message.success(`Moved ${completed.length} task(s) to Todo`);
                        } catch (err) {
                            message.error(
                                err?.response?.data?.message || err?.message || 'Failed to move tasks to Todo',
                            );
                        }
                    })();
                    return;
                }
                if (targetKey === 'submitted') {
                    if (completed.length === 0) {
                        message.info('No tasks in Completed');
                        return;
                    }
                    const entries = taskEntriesFrom(completed);
                    if (entries.length === 0) {
                        message.info('No valid tasks to add');
                        return;
                    }
                    addTasksToSubmissionBatch(entries);
                    openSubmissionPanel();
                    message.success(`Added ${entries.length} task(s) to submission`);
                    return;
                }
                if (targetKey === 'consultation') {
                    if (completed.length === 0) {
                        message.info('No tasks in Completed');
                        return;
                    }
                    const fullSprint = kanban.selectedSprint;
                    completed.forEach((task) => {
                        const tid = getSprintTaskApiId(task) ?? task?.id ?? task?.taskId;
                        addToConsultation({
                            type: 'task',
                            id: tid,
                            name: task?.title ?? task?.name ?? 'Task',
                            sprintId,
                            fullSprint,
                            fullTask: task,
                        });
                    });
                    openConsultationModal();
                }
            }
        },
        [kanban.selectedSprint, completeTask, revertTaskToTodo, addTasksToSubmissionBatch, openSubmissionPanel, canMarkTaskTodo, addToConsultation, openConsultationModal],
    );

    const isTabLoading = useSprintsEnabled && isSprintQueryLoading && (useMilestonesEnabled ? !!selectedMilestoneId : (usePhasesEnabled ? !!selectedMilestoneId : true));

    const isSprintKanbanFetching = Boolean(
        (useSprintsEnabled && hasSprints)
            ? (sprintTabSelection.selectedSprintIdResolved &&
               canViewSprints &&
               (sprintDetailPending ||
                isSprintQueryLoading ||
                (sprintDetailFetching && sprintDetailData === undefined) ||
                (isSprintQueryFetching && fetchedSprintsData === undefined)))
            : projectTasksPending || (projectTasksFetching && projectTasks.length === 0)
    );

    return {
        project,
        milestones,
        milestonesLoading: false,
        hasMilestones,
        hasSprints,
        useSprintsEnabled,
        sprintsLoading: false,
        isTabLoading,
        isSprintKanbanFetching,
        goToMilestonesTab,
        createSprint,
        createSprintPending,
        sprints: sprintsEnrichedForDisplay,
        modals,
        kanban,
        celebratingTaskKey,
        setCelebratingTaskKey,
        handleMarkTaskDone,
        handleRevertToTodo,
        handleColumnFlowAction,
        canCreateSprint,
        canEditSprint,
        canDeleteSprint,
        canCreateTask,
        canEditTask,
        canDeleteTask,
        canMarkTaskTodo,
        canAssignTask,
        taskForAssignModal,
        taskAssignPicklistUsers,
        openTaskAssignModal,
        closeTaskAssignModal,
        submitTaskAssign,
        assignTaskPending,
        createSprintModalOpen,
        setCreateSprintModalOpen,
        sprintTaskDetail,
        openSprintTaskDetail,
        openSprintTaskForEdit,
        closeSprintTaskDetail,
        refreshSprintTaskDetail,
        sprintTaskDetailMarkdown,
        sprintTaskDetailBugs,
        sprintTaskDetailLoading,
        updateTaskPending,
        handleDeleteSprintTask,
        deleteTaskPending,
        handleDeleteSprint,
        deleteSprintPending,
        submissionPanelOpen,
        closeSubmissionPanel,
        submissionSession,
        submittedItems,
        submissionNotes,
        setSubmissionNotes,
        submissionPendingFiles,
        submissionPendingLinks,
        submissionLinkModalOpen,
        submissionLinkForm,
        handleSubmissionFilesSelected,
        handleSubmissionLinkRequest,
        handleSubmissionLinkModalSubmit,
        closeSubmissionLinkModal,
        removeSubmissionPendingFile,
        removeSubmissionPendingLink,
        handleSubmissionEditorLinkRemoved,
        submitToQA,
        submitToQAPending,
        removeFromSubmission,
        submissionGroupedBySprintEnriched,
        sprintSubmittedSubmissionGroups,
        sprintSubmittedOrphanTasks,
        submittedTo,
        setSubmittedTo,
        isQAEnabled,
        isPMOEnabled,
        consultation,
        phases,
        usePhasesEnabled,
        useMilestonesEnabled,
    };
}
